import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import {
  doc, getDoc, collection, getDocs, query, where
} from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, Avatar,
  Chip, CircularProgress, Divider, IconButton, Menu, MenuItem, Tooltip, Button
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BusinessIcon from '@mui/icons-material/Business';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import GavelIcon from '@mui/icons-material/Gavel';
import SettingsIcon from '@mui/icons-material/Settings';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import BarChartIcon from '@mui/icons-material/BarChart';
import PsychologyIcon from '@mui/icons-material/Psychology';
import FeedbackIcon from '@mui/icons-material/Feedback';
import PersonAddDisabledIcon from '@mui/icons-material/PersonAddDisabled';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import FolderIcon from '@mui/icons-material/Folder';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ArticleIcon from '@mui/icons-material/Article';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import CampaignIcon from '@mui/icons-material/Campaign';
import GroupsIcon from '@mui/icons-material/Groups';
import PeopleIcon from '@mui/icons-material/People';
import Badge from '@mui/material/Badge';
import LagbasLogo from '../components/LagbasLogo';
import { useCompanyLogo } from '../hooks/useQRLogo';
import TrainIcon from '@mui/icons-material/Train';
import SubwayIcon from '@mui/icons-material/Subway';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import TramIcon from '@mui/icons-material/Tram';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TvIcon from '@mui/icons-material/Tv';
import ContactsIcon from '@mui/icons-material/Contacts';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ApiIcon from '@mui/icons-material/Api';
import CategoryIcon from '@mui/icons-material/Category';
import EventIcon from '@mui/icons-material/Event';
import MapIcon from '@mui/icons-material/Map';
import BadgeIcon from '@mui/icons-material/Badge';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import GroupIcon from '@mui/icons-material/Group';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import DescriptionIcon from '@mui/icons-material/Description';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PollIcon from '@mui/icons-material/Poll';
import GridViewIcon from '@mui/icons-material/GridView';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import LayersIcon from '@mui/icons-material/Layers';
import FilterListIcon from '@mui/icons-material/FilterList';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import {
  OFFICES, OFFICE_STOPS, getLineColor, fetchDepartures, parseDepartures,
  officeFromBasort, parseLineLabel, categoryToIcon, getTypeLabel,
} from '../utils/departureApi';
import { calcProgress } from '../utils/onboardingTemplate';

const roleLabels = {
  superadmin: 'Superadmin',
  vd: 'VD',
  ao_ansvarig: 'AO-ansvarig',
  gruppledare: 'Gruppledare',
  medarbetare: 'Medarbetare',
  marknadsansvarig: 'Marknadsansvarig',
  marknadskonsult: 'Marknadskonsult',
};

const roleColors = {
  superadmin: 'error',
  vd: 'secondary',
  ao_ansvarig: 'warning',
  gruppledare: 'success',
  medarbetare: 'default',
  marknadsansvarig: 'info',
  marknadskonsult: 'info',
};

export default function Dashboard() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const companyLogo = useCompanyLogo();
  const [userProfile, setUserProfile] = useState(null);
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState({
    tenders: 0, wonTenders: 0, referenceProjects: 0,
    teamMembers: 0, pendingFeedback: 0, activeProjects: 0, offers: 0, workshops: 0
  });
  const [loading, setLoading] = useState(true);
  const [unreadIUPCount, setUnreadIUPCount] = useState(0);
  const [myOnboarding, setMyOnboarding] = useState(null); // eget onboarding-dokument (för medarbetare)
  const [previewRole, setPreviewRole] = useState(
    () => sessionStorage.getItem('previewRole') || null
  );
  const [roleMenuAnchor, setRoleMenuAnchor] = useState(null);

  useEffect(() => {
    async function load() {
      const [userSnap, profileSnap] = await Promise.all([
        getDoc(doc(db, 'users', currentUser.uid)),
        getDoc(doc(db, 'profiles', currentUser.uid)),
      ]);

      const userData = userSnap.exists() ? userSnap.data() : {};
      setUserProfile(userData);
      if (profileSnap.exists()) setProfile(profileSnap.data());

      try {
        const [tenderSnap, refSnap, memberSnap, feedbackSnap, activeProjectsSnap, offersSnap, workshopsSnap] =
          await Promise.all([
            getDocs(collection(db, 'tenders')),
            getDocs(collection(db, 'referenceProjects')),
            userData.departmentId
              ? getDocs(query(collection(db, 'users'),
                  where('departmentId', '==',
                    userData.departmentId)))
              : Promise.resolve({ docs: [] }),
            getDocs(collection(db, 'feedback')),
            getDocs(query(collection(db, 'projects'),
              where('status', '==', 'aktiv'))),
            getDocs(collection(db, 'offers')),
            getDocs(collection(db, 'workshops')),
          ]);

        const tenders = tenderSnap.docs.map(d => d.data());
        const deptTenders = userData.role === 'superadmin'
          ? tenders
          : tenders.filter(t =>
              t.departmentId === userData.departmentId ||
              t.companyId === userData.companyId);

        // Räkna feedback som inte är hanterad
        const pendingFeedback = feedbackSnap.docs.filter(d => {
          const s = d.data().status;
          return !s || s === 'ny_kommentar';
        }).length;

        setStats({
          tenders: deptTenders.length,
          wonTenders: deptTenders.filter(
            t => t.status === 'won').length,
          referenceProjects: refSnap.docs.length,
          teamMembers: memberSnap.docs.length,
          pendingFeedback,
          activeProjects: activeProjectsSnap.docs.length,
          offers: offersSnap.docs.length,
          workshops: workshopsSnap.docs.length,
        });

        // IUP-notifieringar
        try {
          const notifSnap = await getDocs(
            query(
              collection(db, 'notifications'),
              where('toUserId', '==', currentUser.uid),
              where('read', '==', false)
            )
          );
          setUnreadIUPCount(notifSnap.docs.length);
        } catch (_) {}

        // Eget onboarding-dokument (visas i dashboarden tills 100%)
        try {
          const obSnap = await getDocs(query(
            collection(db, 'onboarding'),
            where('employeeUid', '==', currentUser.uid)
          ));
          if (!obSnap.empty) {
            const docs = obSnap.docs.map(d => ({ id: d.id, ...d.data() }))
              .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
            setMyOnboarding(docs[0]);
          }
        } catch (_) {}
      } catch (err) {
        console.error(err);
      }

      setLoading(false);
    }
    load();
  }, [currentUser]);

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  const actualRole = userProfile?.role;
  const role = previewRole || actualRole;
  const isSuperadmin = role === 'superadmin' || role === 'vd';
  const isGroupledare = role === 'superadmin' || role === 'vd' || role === 'ao_ansvarig' || role === 'gruppledare';
  const isAdmin = isSuperadmin || isGroupledare || userProfile?.isAdmin;
  const isEmployee = role === 'medarbetare' || role === 'marknadskonsult' || role === 'marknadsansvarig';
  const canManageSubcontractors = isAdmin || userProfile?.isAnbudsansvarig || isEmployee;

  function activatePreviewRole(r) {
    sessionStorage.setItem('previewRole', r);
    setPreviewRole(r);
    setRoleMenuAnchor(null);
  }

  function resetPreviewRole() {
    sessionStorage.removeItem('previewRole');
    setPreviewRole(null);
  }

  const anbudStats = [
    { label: 'Totalt', value: stats.tenders },
    { label: 'Vunna', value: stats.wonTenders },
    { label: 'Vinstprocent', value: stats.tenders > 0 && stats.wonTenders > 0
        ? Math.round((stats.wonTenders / stats.tenders) * 100) + '%' : '-' },
  ];

  // Gruppfärger
  const C_AFFAR        = '#FF6B00';
  const C_UPPDRAG      = '#2196F3';
  const C_MEDARBETARE  = '#4CAF50';
  const C_FACILITERING = '#9C27B0';
  const C_VERKTYG      = '#607D8B';
  const C_EKONOMI      = '#01579B';

  const panelSx = (color) => ({
    p: 3, mb: 2, cursor: 'pointer', borderRadius: 2,
    background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
    color: 'white',
    transition: 'transform 0.2s, box-shadow 0.2s',
    borderLeft: '4px solid transparent',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `0 8px 24px rgba(0,0,0,0.3)`,
      borderLeft: `4px solid ${color}`,
    },
  });

  const chipSx = (color) => ({
    bgcolor: 'rgba(255,255,255,0.08)', color: '#ccc', cursor: 'pointer',
    '&:hover': { bgcolor: color + '33', color: 'white' },
  });

  const chipIcon = (Icon, color) => <Icon sx={{ fontSize: '1rem !important', color: `${color} !important` }} />;

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>

      {/* PREVIEW-BANNER */}
      {previewRole && (
        <Box sx={{
          bgcolor: '#FF6B00', color: 'white',
          py: 0.75, px: 4,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <Typography variant="body2" fontWeight="bold">
            👁 Förhandsgranskar som: {roleLabels[previewRole]}
          </Typography>
          <Chip
            label="Återställ till Superadmin"
            size="small"
            onClick={resetPreviewRole}
            sx={{
              bgcolor: 'white', color: '#FF6B00',
              fontWeight: 'bold', cursor: 'pointer',
              '&:hover': { bgcolor: '#ffe0cc' }
            }}
          />
        </Box>
      )}

      {/* HEADER */}
      <Box sx={{
        backgroundColor: '#1a1a1a', color: 'white', py: 2, px: 4
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LagbasLogo size="1.6rem" />
            <Divider orientation="vertical" flexItem
              sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                {userProfile?.name ||
                  currentUser.displayName || 'Användare'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={roleLabels[role] || 'Medarbetare'}
                  color={previewRole ? 'default' : (roleColors[role] || 'default')}
                  size="small"
                  onClick={actualRole === 'superadmin'
                    ? e => setRoleMenuAnchor(e.currentTarget)
                    : undefined}
                  sx={{
                    height: 18, fontSize: '0.65rem',
                    ...(previewRole && {
                      bgcolor: '#FF6B00', color: 'white'
                    }),
                    ...(actualRole === 'superadmin' && {
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.8 }
                    })
                  }}
                />
                {actualRole === 'superadmin' && !previewRole && (
                  <Typography variant="caption"
                    sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.6rem' }}>
                    klicka för att förhandsgranska
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Önskemål och Feedback – Rapportera fel eller önska nya funktioner" arrow>
              <IconButton color="inherit" onClick={() => navigate('/feedback')} size="small">
                <FeedbackIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Appinställningar – Tema, utseende och preferenser" arrow>
              <IconButton color="inherit" onClick={() => navigate('/settings')} size="small">
                <SettingsIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            {isAdmin && (
              <Tooltip title={`Administration${stats.pendingFeedback > 0 ? ` · ${stats.pendingFeedback} feedback` : ''}`} arrow>
                <IconButton color="inherit" onClick={() => navigate('/admin')} size="small"
                  sx={{
                    border: '1px solid rgba(255,107,0,0.35)',
                    borderRadius: 1.5,
                    px: 0.75,
                    transition: 'all 0.15s',
                    '&:hover': {
                      bgcolor: 'rgba(255,107,0,0.15)',
                      border: '1px solid #FF6B00',
                    }
                  }}>
                  <Badge badgeContent={stats.pendingFeedback > 0 ? stats.pendingFeedback : null} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.55rem', minWidth: 14, height: 14 } }}>
                    <AdminPanelSettingsIcon sx={{ fontSize: 20, color: '#FF6B00' }} />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}
            {isAdmin && (
              <Tooltip title="Företagsinställningar – Logotyp och företagsuppgifter" arrow>
                <IconButton color="inherit" onClick={() => navigate('/company')} size="small">
                  <BusinessIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}
            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: 0.5 }} />
            {profile?.personal?.photo && (
              <Avatar src={profile.personal.photo}
                sx={{ width: 36, height: 36 }} />
            )}
            <IconButton color="inherit" onClick={handleLogout}
              title="Logga ut">
              <LogoutIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* ROLL-MENY */}
      <Menu
        anchorEl={roleMenuAnchor}
        open={!!roleMenuAnchor}
        onClose={() => setRoleMenuAnchor(null)}
        PaperProps={{ sx: { minWidth: 200 } }}
      >
        <MenuItem disabled>
          <Typography variant="caption" color="text.secondary">
            Förhandsgranska som...
          </Typography>
        </MenuItem>
        {Object.entries(roleLabels)
          .filter(([r]) => r !== 'superadmin')
          .map(([r, label]) => (
            <MenuItem
              key={r}
              selected={previewRole === r}
              onClick={() => activatePreviewRole(r)}
            >
              <Chip
                label={label}
                color={roleColors[r] || 'default'}
                size="small"
                sx={{ mr: 1, pointerEvents: 'none' }}
              />
              {label}
            </MenuItem>
          ))
        }
        {previewRole && (
          <MenuItem onClick={resetPreviewRole} sx={{ color: '#FF6B00', fontWeight: 'bold' }}>
            Återställ till Superadmin
          </MenuItem>
        )}
      </Menu>

      {/* ── HERO/BANNER ─────────────────────────────────────────────────── */}
      <Box sx={{
        position: 'relative', overflow: 'hidden',
        background: 'linear-gradient(135deg, #0f0f0f 0%, #1a0a00 50%, #0f0f0f 100%)',
        borderBottom: '1px solid rgba(255,107,0,0.15)',
        px: { xs: 3, md: 6 }, py: { xs: 3, md: 4 },
      }}>
        {/* Subtilt bakgrundsmönster */}
        <Box sx={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 20% 50%, rgba(255,107,0,0.07) 0%, transparent 60%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
          {companyLogo && (
            <Box component="img" src={companyLogo} alt="Logotyp"
              sx={{ height: 48, maxWidth: 160, objectFit: 'contain', filter: 'brightness(1.1)' }} />
          )}
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography fontWeight={900} sx={{ fontSize: { xs: '1.4rem', md: '1.8rem' }, color: 'white', lineHeight: 1.1, fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>
              Välkommen, {userProfile?.name?.split(' ')[0] || currentUser.displayName?.split(' ')[0] || 'igen'}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.82rem', mt: 0.5 }}>
              Navigera snabbt till dina moduler
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {canManageSubcontractors && (
              <Chip label="Affär & Anbud" size="small" onClick={() => {}} sx={{ bgcolor: '#FF6B0022', color: '#FF9966', border: '1px solid #FF6B0044', cursor: 'pointer', '&:hover': { bgcolor: '#FF6B0033' } }} />
            )}
            <Chip label="Facilitering" size="small" onClick={() => {}} sx={{ bgcolor: '#9C27B022', color: '#CE93D8', border: '1px solid #9C27B044', cursor: 'pointer', '&:hover': { bgcolor: '#9C27B033' } }} />
            <Chip label="Medarbetare" size="small" onClick={() => {}} sx={{ bgcolor: '#4CAF5022', color: '#81C784', border: '1px solid #4CAF5044', cursor: 'pointer', '&:hover': { bgcolor: '#4CAF5033' } }} />
            {canManageSubcontractors && (
              <Chip label="UppdragN" size="small" onClick={() => navigate('/projects')} sx={{ bgcolor: '#2196F322', color: '#64B5F6', border: '1px solid #2196F344', cursor: 'pointer', '&:hover': { bgcolor: '#2196F333' } }} />
            )}
          </Box>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 4, pb: 6 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3, alignItems: 'start' }}>

          {/* ═══ VÄNSTER KOLUMN ═══════════════════════════════════════════════ */}
          <Box>
            {/* ── AFFÄR & ANBUD ────────────────────────────────────────────── */}
            <SectionHeader label="Affär & Anbud" color={C_AFFAR} />

            {/* Affärer */}
            {canManageSubcontractors && (
              <Paper onClick={() => navigate('/affarer')} sx={panelSx(C_AFFAR)}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <StorefrontIcon sx={{ fontSize: 32, color: C_AFFAR }} />
                    <Box>
                      <Typography variant="h6" fontWeight="bold">Affärer</Typography>
                      <Typography variant="body2" sx={{ color: '#aaa' }}>Kunder, pipeline, produktkatalog, PLING och statistik</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }} onClick={e => e.stopPropagation()}>
                    <Button size="small" variant="outlined" startIcon={<AddIcon />}
                      onClick={() => navigate('/crm/companies?new=1')}
                      sx={{ color: C_AFFAR, borderColor: C_AFFAR, whiteSpace: 'nowrap', fontSize: '0.75rem', '&:hover': { bgcolor: 'rgba(255,107,0,0.1)' } }}>
                      Ny kund
                    </Button>
                    <Button size="small" variant="outlined" startIcon={<AddIcon />}
                      onClick={() => navigate('/crm/leads?new=1')}
                      sx={{ color: C_AFFAR, borderColor: C_AFFAR, whiteSpace: 'nowrap', fontSize: '0.75rem', '&:hover': { bgcolor: 'rgba(255,107,0,0.1)' } }}>
                      Nytt lead
                    </Button>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                  <Chip icon={chipIcon(BusinessIcon, C_AFFAR)} label="Kunder" size="small" onClick={() => navigate('/crm/companies')} sx={chipSx(C_AFFAR)} />
                  <Chip icon={chipIcon(ContactsIcon, C_AFFAR)} label="Leads" size="small" onClick={() => navigate('/crm/leads')} sx={chipSx(C_AFFAR)} />
                  <Chip icon={chipIcon(NotificationsActiveIcon, C_AFFAR)} label="PLING" size="small" onClick={() => navigate('/crm/pling-klang')} sx={chipSx(C_AFFAR)} />
                  <Chip icon={chipIcon(CategoryIcon, C_AFFAR)} label="Produkter" size="small" onClick={() => navigate('/produkter')} sx={chipSx(C_AFFAR)} />
                  <Chip icon={chipIcon(BarChartIcon, C_AFFAR)} label="Statistik" size="small" onClick={() => navigate('/affarer/statistik')} sx={chipSx(C_AFFAR)} />
                  <Chip icon={chipIcon(EventIcon, C_AFFAR)} label="Kalender" size="small" onClick={() => navigate('/affarer/kalender')} sx={chipSx(C_AFFAR)} />
                  <Chip icon={chipIcon(MapIcon, C_AFFAR)} label="Karta" size="small" onClick={() => navigate('/affarer/karta')} sx={chipSx(C_AFFAR)} />
                </Box>
              </Paper>
            )}

            {/* Upphandlingar API */}
            <Paper onClick={() => navigate('/upphandlingar')} sx={panelSx(C_AFFAR)}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ApiIcon sx={{ fontSize: 32, color: C_AFFAR }} />
                <Box>
                  <Typography variant="h6" fontWeight="bold">Upphandlingar API</Typography>
                  <Typography variant="body2" sx={{ color: '#aaa' }}>Konsultförfrågningar från upphandlingsportaler med sök och larmregler</Typography>
                </Box>
              </Box>
            </Paper>

            {/* Anbud */}
            <Paper onClick={() => navigate('/tenders')} sx={panelSx(C_AFFAR)}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <GavelIcon sx={{ fontSize: 32, color: C_AFFAR }} />
                  <Box>
                    <Typography variant="h6" fontWeight="bold">Anbud</Typography>
                    <Typography variant="body2" sx={{ color: '#aaa' }}>Hantera anbud, konsultmatchning och anbudsprocess</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                  {anbudStats.map((s, i) => (
                    <Box key={i} sx={{ textAlign: 'center' }}>
                      <Typography fontWeight={900} sx={{ fontSize: '1.5rem', color: C_AFFAR, lineHeight: 1 }}>{s.value}</Typography>
                      <Typography variant="caption" sx={{ color: '#888' }}>{s.label}</Typography>
                    </Box>
                  ))}
                  <Button size="small" variant="outlined" startIcon={<AddIcon />}
                    onClick={e => { e.stopPropagation(); navigate('/tenders?new=1'); }}
                    sx={{ color: C_AFFAR, borderColor: C_AFFAR, whiteSpace: 'nowrap', '&:hover': { bgcolor: 'rgba(255,107,0,0.1)' } }}>
                    Nytt
                  </Button>
                </Box>
              </Box>
            </Paper>

            {/* Offerter */}
            {canManageSubcontractors && (
              <Paper onClick={() => navigate('/offers')} sx={panelSx(C_AFFAR)}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <ArticleIcon sx={{ fontSize: 32, color: C_AFFAR }} />
                    <Box>
                      <Typography variant="h6" fontWeight="bold">Offerter</Typography>
                      <Typography variant="body2" sx={{ color: '#aaa' }}>Skapa och hantera offerter med mallar och PDF-export</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {stats.offers > 0 && (
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography fontWeight={900} sx={{ fontSize: '1.5rem', color: C_AFFAR, lineHeight: 1 }}>{stats.offers}</Typography>
                        <Typography variant="caption" sx={{ color: '#888' }}>Totalt</Typography>
                      </Box>
                    )}
                    <Button size="small" variant="outlined" startIcon={<AddIcon />}
                      onClick={e => { e.stopPropagation(); navigate('/offers?new=1'); }}
                      sx={{ color: C_AFFAR, borderColor: C_AFFAR, whiteSpace: 'nowrap', '&:hover': { bgcolor: 'rgba(255,107,0,0.1)' } }}>
                      Nytt
                    </Button>
                  </Box>
                </Box>
              </Paper>
            )}

            {/* Avtalsregister */}
            {(isSuperadmin || role === 'ao_ansvarig' || isEmployee) && (
              <Paper onClick={() => navigate('/avtal')} sx={panelSx(C_AFFAR)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <GavelIcon sx={{ fontSize: 32, color: C_AFFAR }} />
                  <Box>
                    <Typography variant="h6" fontWeight="bold">Avtalsregister</Typography>
                    <Typography variant="body2" sx={{ color: '#aaa' }}>Kund- och leverantörsavtal, villkor, påminnelser – digital signering utvärderas</Typography>
                  </Box>
                </Box>
              </Paper>
            )}

            {/* Orderbekräftelser */}
            {canManageSubcontractors && (
              <Paper onClick={() => navigate('/orderbekraftelser')} sx={panelSx(C_AFFAR)}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FactCheckIcon sx={{ fontSize: 32, color: C_AFFAR }} />
                    <Box>
                      <Typography variant="h6" fontWeight="bold">Orderbekräftelser</Typography>
                      <Typography variant="body2" sx={{ color: '#aaa' }}>Bekräfta konsultuppdrag — hämtar data från offert, kund och budget automatiskt</Typography>
                    </Box>
                  </Box>
                  <Button size="small" variant="outlined" startIcon={<AddIcon />}
                    onClick={e => { e.stopPropagation(); navigate('/orderbekraftelser'); }}
                    sx={{ color: C_AFFAR, borderColor: C_AFFAR, whiteSpace: 'nowrap', '&:hover': { bgcolor: 'rgba(255,107,0,0.1)' } }}>
                    Ny
                  </Button>
                </Box>
              </Paper>
            )}

            {/* ── FACILITERING & ANALYS ────────────────────────────────────── */}
            <Box sx={{ mt: 4 }}>
              <SectionHeader label="Facilitering & Analys" color={C_FACILITERING} />

              {/* WorkshopN */}
              <Paper onClick={() => navigate('/workshopn')} sx={panelSx(C_FACILITERING)}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <GroupsIcon sx={{ fontSize: 32, color: C_FACILITERING }} />
                    <Box>
                      <Typography variant="h6" fontWeight="bold">Workshop<span style={{ color: C_FACILITERING }}>N</span></Typography>
                      <Typography variant="body2" sx={{ color: '#aaa' }}>Digitala och hybrida workshops — realtidssvar, AI-tolkning och live-presentation</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {stats.workshops > 0 && (
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography fontWeight={900} sx={{ fontSize: '1.5rem', color: C_FACILITERING, lineHeight: 1 }}>{stats.workshops}</Typography>
                        <Typography variant="caption" sx={{ color: '#888' }}>Totalt</Typography>
                      </Box>
                    )}
                    <Button size="small" variant="outlined" startIcon={<AddIcon />}
                      onClick={e => { e.stopPropagation(); navigate('/workshopn/new'); }}
                      sx={{ color: C_FACILITERING, borderColor: C_FACILITERING, whiteSpace: 'nowrap', '&:hover': { bgcolor: C_FACILITERING + '1a' } }}>
                      Nytt
                    </Button>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                  <Chip icon={chipIcon(CloudUploadIcon, C_FACILITERING)} label="Realtidssvar" size="small" onClick={() => navigate('/workshopn')} sx={chipSx(C_FACILITERING)} />
                  <Chip icon={chipIcon(QrCodeScannerIcon, C_FACILITERING)} label="Hybridläge – QR & AI-skanning" size="small" onClick={() => navigate('/workshopn')} sx={chipSx(C_FACILITERING)} />
                  <Chip icon={chipIcon(SlideshowIcon, C_FACILITERING)} label="Live-presentation" size="small" onClick={() => navigate('/workshopn')} sx={chipSx(C_FACILITERING)} />
                  <Chip icon={chipIcon(AutoAwesomeIcon, C_FACILITERING)} label="AI tolkar handskrivet" size="small" onClick={() => navigate('/workshopn')} sx={chipSx(C_FACILITERING)} />
                  <Chip icon={chipIcon(PollIcon, C_FACILITERING)} label="Fritext, skala, flerval & ordmoln" size="small" onClick={() => navigate('/workshopn')} sx={chipSx(C_FACILITERING)} />
                  <Chip icon={chipIcon(GroupIcon, C_FACILITERING)} label="Grupphantering" size="small" onClick={() => navigate('/workshopn')} sx={chipSx(C_FACILITERING)} />
                </Box>
              </Paper>

              {/* SwotN */}
              <Paper onClick={() => navigate('/swotn')} sx={panelSx(C_FACILITERING)}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <GridViewIcon sx={{ fontSize: 32, color: C_FACILITERING }} />
                    <Box>
                      <Typography variant="h6" fontWeight="bold">Swot<span style={{ color: C_FACILITERING }}>N</span></Typography>
                      <Typography variant="body2" sx={{ color: '#aaa' }}>Digital SWOT-analys — styrkor, svagheter, möjligheter och hot</Typography>
                    </Box>
                  </Box>
                  <Button size="small" variant="outlined" startIcon={<AddIcon />}
                    onClick={e => { e.stopPropagation(); navigate('/swotn'); }}
                    sx={{ color: C_FACILITERING, borderColor: C_FACILITERING, whiteSpace: 'nowrap', '&:hover': { bgcolor: C_FACILITERING + '1a' } }}>
                    Ny SWOT
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                  <Chip icon={chipIcon(GridViewIcon, C_FACILITERING)} label="S-W-O-T board" size="small" onClick={() => navigate('/swotn')} sx={chipSx(C_FACILITERING)} />
                  <Chip icon={chipIcon(QrCodeScannerIcon, C_FACILITERING)} label="Hybridblad & AI-skanning" size="small" onClick={() => navigate('/swotn')} sx={chipSx(C_FACILITERING)} />
                  <Chip icon={chipIcon(AutoAwesomeIcon, C_FACILITERING)} label="AI-analys med strategikorsningar" size="small" onClick={() => navigate('/swotn')} sx={chipSx(C_FACILITERING)} />
                  <Chip icon={chipIcon(GroupsIcon, C_FACILITERING)} label="Länkas till WorkshopN" size="small" onClick={() => navigate('/swotn')} sx={chipSx(C_FACILITERING)} />
                </Box>
              </Paper>
            </Box>

            {/* ── APPLAB ───────────────────────────────────────────────────── */}
            <Box sx={{ mt: 4 }}>
              <SectionHeader label="AppLAB" color="#00897B" />
              <Paper onClick={() => navigate('/applab')} sx={panelSx('#00897B')}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                  <RocketLaunchIcon sx={{ fontSize: 32, color: '#00897B' }} />
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      App<span style={{ color: '#FF6B00' }}>LAB</span>
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#aaa' }}>Intern verktygsplattform – alla LagbasN-moduler på ett ställe</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip icon={chipIcon(LayersIcon, '#00897B')} label="Appkatalog" size="small" onClick={() => navigate('/applab')} sx={chipSx('#00897B')} />
                  <Chip icon={chipIcon(FilterListIcon, '#00897B')} label="Filtrera & sök" size="small" onClick={() => navigate('/applab')} sx={chipSx('#00897B')} />
                  <Chip icon={chipIcon(AutoAwesomeIcon, '#00897B')} label="Statusöversikt" size="small" onClick={() => navigate('/applab')} sx={chipSx('#00897B')} />
                </Box>
              </Paper>
            </Box>

          </Box>

          {/* ═══ HÖGER KOLUMN ═════════════════════════════════════════════════ */}
          <Box>

            {/* ── MEDARBETARE & HR ─────────────────────────────────────────── */}
            <SectionHeader label="Medarbetare & HR" color={C_MEDARBETARE} />

            {/* Rekrytering */}
            {(isSuperadmin || role === 'ao_ansvarig' || role === 'vd' || role === 'gruppledare' || isEmployee) && (
              <Paper onClick={() => navigate('/rekrytering')} sx={panelSx(C_MEDARBETARE)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <PersonSearchIcon sx={{ fontSize: 32, color: C_MEDARBETARE }} />
                  <Box>
                    <Typography variant="h6" fontWeight="bold">Rekrytering</Typography>
                    <Typography variant="body2" sx={{ color: '#aaa' }}>Kandidatflöde från ansökan till signerat avtal och ny medarbetare</Typography>
                  </Box>
                </Box>
              </Paper>
            )}

            {/* Onboarding */}
            {(isSuperadmin || role === 'ao_ansvarig' || role === 'vd' || role === 'gruppledare' || isEmployee) && (
              <Paper onClick={() => navigate('/onboarding')} sx={panelSx(C_MEDARBETARE)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <AssignmentTurnedInIcon sx={{ fontSize: 32, color: C_MEDARBETARE }} />
                  <Box>
                    <Typography variant="h6" fontWeight="bold">Onboarding</Typography>
                    <Typography variant="body2" sx={{ color: '#aaa' }}>Introduktionschecklista – utrustning, IT-konton, social introduktion och uppföljning</Typography>
                  </Box>
                </Box>
              </Paper>
            )}

            {/* Medarbetare */}
            <Paper onClick={() => navigate('/hr')} sx={panelSx(C_MEDARBETARE)}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Badge badgeContent={unreadIUPCount > 0 ? unreadIUPCount : null} color="error">
                    <PeopleIcon sx={{ fontSize: 32, color: C_MEDARBETARE }} />
                  </Badge>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">Medarbetare</Typography>
                    <Typography variant="body2" sx={{ color: '#aaa' }}>Register, avtal, onboarding och medarbetarutveckling</Typography>
                  </Box>
                </Box>
                {stats.teamMembers > 0 && (
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography fontWeight={900} sx={{ fontSize: '1.5rem', color: C_MEDARBETARE, lineHeight: 1 }}>{stats.teamMembers}</Typography>
                    <Typography variant="caption" sx={{ color: '#888' }}>I ditt AO</Typography>
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                <Chip icon={chipIcon(PersonIcon, C_MEDARBETARE)} label="Min Profil" size="small" onClick={() => navigate('/profile')} sx={chipSx(C_MEDARBETARE)} />
                <Chip icon={chipIcon(AssignmentIcon, C_MEDARBETARE)} label="Profilrapport" size="small" onClick={() => navigate('/cv-preview')} sx={chipSx(C_MEDARBETARE)} />
                <Chip icon={chipIcon(BadgeIcon, C_MEDARBETARE)} label="Medarbetarregister" size="small" onClick={() => navigate('/employees')} sx={chipSx(C_MEDARBETARE)} />
                {isGroupledare && (
                  <Chip icon={chipIcon(GavelIcon, C_MEDARBETARE)} label="Avtal" size="small" onClick={() => navigate('/avtal')} sx={chipSx(C_MEDARBETARE)} />
                )}
                {isGroupledare ? (
                  <Chip icon={chipIcon(AssignmentTurnedInIcon, C_MEDARBETARE)} label="Onboarding" size="small" onClick={() => navigate('/onboarding')} sx={chipSx(C_MEDARBETARE)} />
                ) : myOnboarding && calcProgress(myOnboarding.checks) < 100 ? (
                  <Chip icon={chipIcon(AssignmentTurnedInIcon, C_MEDARBETARE)}
                    label={`Min onboarding ${calcProgress(myOnboarding.checks)}%`} size="small"
                    onClick={() => navigate(`/onboarding/${myOnboarding.id}`)} sx={chipSx(C_MEDARBETARE)} />
                ) : null}
                <Chip icon={chipIcon(RecordVoiceOverIcon, C_MEDARBETARE)} label="Mitt IUP" size="small"
                  onClick={() => navigate(
                    (role === 'vd' || (role === 'superadmin' && !userProfile?.departmentId))
                      ? '/hr/forvantan' : '/hr/iup'
                  )} sx={chipSx(C_MEDARBETARE)} />
                {isGroupledare && (
                  <Chip icon={chipIcon(GroupIcon, C_MEDARBETARE)} label="IUP-team" size="small" onClick={() => navigate('/hr/team')} sx={chipSx(C_MEDARBETARE)} />
                )}
                {isGroupledare && (
                  <Chip icon={chipIcon(BarChartIcon, C_MEDARBETARE)} label="GAP-analys" size="small" onClick={() => navigate('/gap-analysis')} sx={chipSx(C_MEDARBETARE)} />
                )}
                {isGroupledare && (
                  <Chip icon={chipIcon(PsychologyIcon, C_MEDARBETARE)} label="Kompetenshantering" size="small" onClick={() => navigate('/skills')} sx={chipSx(C_MEDARBETARE)} />
                )}
                {isAdmin && (
                  <Chip icon={chipIcon(DescriptionIcon, C_MEDARBETARE)} label="IUP-mall" size="small" onClick={() => navigate('/hr/iup-mall')} sx={chipSx(C_MEDARBETARE)} />
                )}
                {canManageSubcontractors && (
                  <Chip icon={chipIcon(PersonAddDisabledIcon, C_MEDARBETARE)} label="Underkonsulter" size="small" onClick={() => navigate('/subcontractors')} sx={chipSx(C_MEDARBETARE)} />
                )}
              </Box>
            </Paper>

            {/* ── UPPDRAGN ─────────────────────────────────────────────────── */}
            <Box sx={{ mt: 4 }}>
              <SectionHeader label="UppdragN" color={C_UPPDRAG} />

              {canManageSubcontractors && (
                <Paper onClick={() => navigate('/projects')} sx={panelSx(C_UPPDRAG)}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <FolderOpenIcon sx={{ fontSize: 32, color: C_UPPDRAG }} />
                      <Box>
                        <Typography variant="h6" fontWeight="bold">Uppdrag<span style={{ color: C_UPPDRAG }}>N</span></Typography>
                        <Typography variant="body2" sx={{ color: '#aaa' }}>Hantera uppdrag med budget och Milltime-utfall</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography fontWeight={900} sx={{ fontSize: '1.5rem', color: C_UPPDRAG, lineHeight: 1 }}>{stats.activeProjects}</Typography>
                        <Typography variant="caption" sx={{ color: '#888' }}>Aktiva</Typography>
                      </Box>
                      <Button size="small" variant="outlined" startIcon={<AddIcon />}
                        onClick={e => { e.stopPropagation(); navigate('/projects?new=1'); }}
                        sx={{ color: C_UPPDRAG, borderColor: C_UPPDRAG, whiteSpace: 'nowrap', '&:hover': { bgcolor: C_UPPDRAG + '1a' } }}>
                        Nytt
                      </Button>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                    <Chip icon={chipIcon(FolderOpenIcon, C_UPPDRAG)} label="Uppdragsregister" size="small" onClick={() => navigate('/projects')} sx={chipSx(C_UPPDRAG)} />
                    <Chip icon={chipIcon(MenuBookIcon, C_UPPDRAG)} label="Referensuppdrag" size="small" onClick={() => navigate('/reference-projects')} sx={chipSx(C_UPPDRAG)} />
                  </Box>
                </Paper>
              )}
            </Box>

            {/* ── EKONOMI & UPPFÖLJNING ───────────────────────────────────── */}
            <Box sx={{ mt: 4 }}>
              <SectionHeader label="Ekonomi & Uppföljning" color={C_EKONOMI} />
              <Paper onClick={() => navigate('/framtidn')} sx={panelSx(C_EKONOMI)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TrendingUpIcon sx={{ fontSize: 32, color: C_EKONOMI }} />
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Framtid<span style={{ color: C_EKONOMI }}>N</span>
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#aaa' }}>
                      Resursplanering och beläggning 12 månader framåt
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                  <Chip icon={chipIcon(CalendarViewMonthIcon, C_EKONOMI)} label="Planering" size="small"
                    onClick={() => navigate('/framtidn')} sx={chipSx(C_EKONOMI)} />
                  <Chip icon={chipIcon(BarChartIcon, C_EKONOMI)} label="Beläggningsrapport" size="small"
                    onClick={() => navigate('/framtidn?view=report')} sx={chipSx(C_EKONOMI)} />
                  <Chip icon={chipIcon(AccountBalanceWalletIcon, C_EKONOMI)} label="BudgetN" size="small"
                    onClick={() => navigate('/budgetn')} sx={chipSx(C_EKONOMI)} />
                  <Chip icon={chipIcon(FactCheckIcon, C_EKONOMI)} label="ExternTid" size="small"
                    onClick={() => navigate('/externtid')} sx={chipSx(C_EKONOMI)} />
                </Box>
              </Paper>
            </Box>

            {/* ── KOMMUNIKATION & VERKTYG ──────────────────────────────────── */}
            <Box sx={{ mt: 4 }}>
              <SectionHeader label="Kommunikation & Verktyg" color={C_VERKTYG} />

              {/* Marknad */}
              <Paper onClick={() => navigate('/marketing')} sx={panelSx(C_VERKTYG)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CampaignIcon sx={{ fontSize: 32, color: C_VERKTYG }} />
                  <Box>
                    <Typography variant="h6" fontWeight="bold">Marknad</Typography>
                    <Typography variant="body2" sx={{ color: '#aaa' }}>Mailsignaturer och marknadsverktyg</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                  <Chip icon={chipIcon(BorderColorIcon, C_VERKTYG)} label="Mailsignatur" size="small" onClick={() => navigate('/marketing/signature')} sx={chipSx(C_VERKTYG)} />
                  <Chip icon={chipIcon(LinkedInIcon, C_VERKTYG)} label="LinkedIn-verkstad" size="small" onClick={() => navigate('/marketing/linkedin')} sx={chipSx(C_VERKTYG)} />
                  <Chip icon={chipIcon(QrCodeScannerIcon, C_VERKTYG)} label="QR-KodN" size="small" onClick={() => navigate('/marketing/qrkod')} sx={chipSx(C_VERKTYG)} />
                </Box>
              </Paper>

              {/* CastN */}
              {(isAdmin || isEmployee) && (
                <Paper onClick={() => navigate('/marketing/castn')} sx={panelSx(C_VERKTYG)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TvIcon sx={{ fontSize: 32, color: C_VERKTYG }} />
                    <Box>
                      <Typography variant="h6" fontWeight="bold">Cast<span style={{ color: C_VERKTYG }}>N</span></Typography>
                      <Typography variant="body2" sx={{ color: '#aaa' }}>Digital skärm & presentationsläge — information på storskärm</Typography>
                    </Box>
                  </Box>
                </Paper>
              )}

              {/* PitchN */}
              <Paper onClick={() => navigate('/pitch')} sx={panelSx(C_VERKTYG)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <SlideshowIcon sx={{ fontSize: 32, color: C_VERKTYG }} />
                  <Box>
                    <Typography variant="h6" fontWeight="bold">Pitch<span style={{ color: C_VERKTYG }}>N</span></Typography>
                    <Typography variant="body2" sx={{ color: '#aaa' }}>AI-drivna kundpresentationer med Tikab-design</Typography>
                  </Box>
                </Box>
              </Paper>

              {/* DokumentN */}
              {canManageSubcontractors && (
                <Paper onClick={() => navigate('/document-library')} sx={panelSx(C_VERKTYG)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FolderIcon sx={{ fontSize: 32, color: C_VERKTYG }} />
                    <Box>
                      <Typography variant="h6" fontWeight="bold">Dokument<span style={{ color: C_VERKTYG }}>N</span></Typography>
                      <Typography variant="body2" sx={{ color: '#aaa' }}>Certifieringar, försäkringsbevis och standardfiler</Typography>
                    </Box>
                  </Box>
                </Paper>
              )}
            </Box>

            {/* ── SERVICE ──────────────────────────────────────────────────── */}
            <Box sx={{ mt: 4 }}>
              <SectionHeader label="Service" color={C_VERKTYG} />
              <DepartureWidget office={userProfile?.office || officeFromBasort(userProfile?.basort) || 'stockholm'} navigate={navigate} />
            </Box>

          </Box>
        </Box>
      </Container>
    </Box>
  );
}

// ── Sektionsrubrik ─────────────────────────────────────────────────────────────
function SectionHeader({ label, color }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, pb: 1.5, borderBottom: `1px solid ${color}22` }}>
      <Box sx={{ width: 4, height: 22, borderRadius: 2, bgcolor: color, flexShrink: 0 }} />
      <Typography fontWeight={800} fontSize="0.8rem" color={color} letterSpacing={1.5} sx={{ textTransform: 'uppercase' }}>
        {label}
      </Typography>
    </Box>
  );
}

// ── Avgångswidget ──────────────────────────────────────────────────────────────

function DepModeIcon({ icon }) {
  const sz = '1rem';
  if (icon === 'metro') return <SubwayIcon sx={{ fontSize: sz }} />;
  if (icon === 'bus') return <DirectionsBusIcon sx={{ fontSize: sz }} />;
  if (icon === 'tram') return <TramIcon sx={{ fontSize: sz }} />;
  return <TrainIcon sx={{ fontSize: sz }} />;
}

function DepartureWidget({ office, navigate }) {
  const [rows, setRows] = useState([]);  // [{stopName, icon, lineNum, dir, category, mins[]}]
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const stops = OFFICE_STOPS[office] || [];
        const results = await Promise.allSettled(
          stops.slice(0, 2).map(s => fetchDepartures(s.id))
        );
        const allRows = [];
        results.forEach((r, i) => {
          if (r.status !== 'fulfilled') return;
          const groups = parseDepartures(r.value).slice(0, 3);
          groups.forEach(g => {
            allRows.push({
              stopName: stops[i].name,
              stopIcon: stops[i].icon,
              lineNum: g.lineNumber,
              dir: g.direction,
              category: g.category,
              mins: g.departures.slice(0, 3),
            });
          });
        });
        setRows(allRows);
      } catch (e) {
        console.error('DepartureWidget:', e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [office]);

  return (
    <Paper
      sx={{
        p: 0, mb: 2, overflow: 'hidden', cursor: 'pointer',
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
        color: 'white',
        transition: 'transform 0.2s, box-shadow 0.2s',
        borderLeft: '4px solid transparent',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
          borderLeft: '4px solid #FF6B00',
        },
      }}
      onClick={() => navigate('/avgangstavla')}
    >
      {/* Rubrik */}
      <Box sx={{
        px: 3, py: 1.5,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TrainIcon sx={{ fontSize: 28, color: '#FF6B00' }} />
          <Box>
            <Typography variant="h6" fontWeight="bold">Avgångar</Typography>
            <Typography variant="body2" sx={{ color: '#aaa' }}>
              {OFFICES[office]?.displayName || 'Stockholm'} · nästa avgångar
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Kiosk-läge (helskärm)">
            <IconButton
              size="small"
              onClick={e => { e.stopPropagation(); navigate('/avgangstavla?kiosk=1'); }}
              sx={{ color: '#555', '&:hover': { color: '#FF6B00' }, p: 0.5 }}
            >
              <TvIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Tooltip>
          <Typography variant="caption" sx={{ color: '#555' }}>Visa alla</Typography>
          <ChevronRightIcon sx={{ fontSize: '1rem', color: '#555' }} />
        </Box>
      </Box>

      {/* Avgångsrader */}
      <Box sx={{ px: 3, py: 1.5 }}>
        {loading && (
          <CircularProgress size={16} sx={{ color: '#555' }} />
        )}
        {!loading && rows.length === 0 && (
          <Typography sx={{ fontSize: '0.8rem', color: '#555' }}>
            Inga avgångar – konfigureras under Admin → Avgångstavla
          </Typography>
        )}
        {rows.slice(0, 6).map((row, i) => {
          const { line, trainType } = parseLineLabel(row.lineNum);
          const typeLabel = trainType || getTypeLabel(row.category);
          const lineColor = getLineColor(line, row.category);
          const modeIconType = categoryToIcon(row.category);
          return (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.7 }}>
              {/* Hållplatsnamn */}
              <Typography sx={{ fontSize: '0.7rem', color: '#64748b', width: 80, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {row.stopName}
              </Typography>
              {/* Trafikslags-ikon */}
              <Box sx={{ width: 16, flexShrink: 0, display: 'flex', justifyContent: 'center', color: '#475569' }}>
                <DepModeIcon icon={modeIconType} />
              </Box>
              {/* Typ-chip – fast bredd för alignment */}
              <Box sx={{
                width: '64px', height: 20, borderRadius: '3px',
                bgcolor: '#334155',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden',
              }}>
                <Typography sx={{ color: '#cbd5e1', fontSize: '0.6rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {typeLabel}
                </Typography>
              </Box>
              {/* Linjechip */}
              <Box sx={{
                minWidth: 22, height: 20, px: 0.5, borderRadius: '3px',
                bgcolor: lineColor,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Typography sx={{ color: '#fff', fontSize: '0.65rem', fontWeight: 800 }}>
                  {line}
                </Typography>
              </Box>
              {/* Riktning */}
              <Typography sx={{ flex: 1, fontSize: '0.78rem', color: '#cbd5e1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {row.dir}
              </Typography>
              {/* Tider */}
              <Box sx={{ display: 'flex', gap: 0.4, flexShrink: 0 }}>
                {row.mins.map((m, j) => (
                  <Box key={j} sx={{
                    minWidth: 26, height: 20, px: 0.4, borderRadius: '3px',
                    bgcolor: m <= 0 ? '#DC2626' : '#1e293b',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Typography sx={{ color: '#fff', fontSize: '0.67rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                      {m <= 0 ? 'Nu' : `${m}m`}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}

